import { Identifiable } from "../../../shared/models/identifiable.model";
import { Lot } from "../../lot/models/lot.model";
import { Palette } from "../../palette/models/palette.model";
import { Controle } from "../../controle/models/controle.model";
import { Chargement } from "../../bon-livraison/models/chargement.model";
import { Commande } from "../../commande/models/commande.model";
import { CommandeClient } from "../../commande-client/models/commande-client.model";
import { Tiers } from "../../tiers/models/tiers.model";
import { Article } from "../../articles/models/article.model";
import { CustomEnum } from "../../custom-enum/models/custom-enum.entity";

export enum TypeNc {
  RECEPTION = "RECEPTION",
  PRODUCTION = "PRODUCTION",
  EXPEDITION = "EXPEDITION",
  /** Le client retourne physiquement de la marchandise (refus, retour). */
  RETOUR_CLIENT = "RETOUR_CLIENT",
  /** Le client se plaint sans retour physique (avoir, refus partiel, plainte qualité). */
  RECLAMATION_CLIENT = "RECLAMATION_CLIENT",
  AUDIT_INTERNE = "AUDIT_INTERNE",
}

/**
 * Origine de la NC : qui est responsable / la cause ?
 * Axe orthogonal au type — un même type peut avoir des origines variées.
 */
export enum OrigineNc {
  INTERNE = "INTERNE",
  EXTERNE = "EXTERNE",
  MIXTE = "MIXTE",
}

/**
 * Détection — où / par qui détectée la première fois.
 * Indicateur ISO 9001 : taux d'échappées = % NC détectées en EXTERNE.
 */
export enum DetectionNc {
  INTERNE = "INTERNE",
  EXTERNE = "EXTERNE",
}

export enum GraviteNc {
  MINEURE = "MINEURE",
  MAJEURE = "MAJEURE",
  CRITIQUE = "CRITIQUE",
}

export enum Cause5M {
  MATIERE = "MATIERE",
  MACHINE = "MACHINE",
  METHODE = "METHODE",
  MAIN_OEUVRE = "MAIN_OEUVRE",
  MILIEU = "MILIEU",
  MESURE = "MESURE",
}

export enum EtatNc {
  OUVERTE = "OUVERTE",
  EN_TRAITEMENT = "EN_TRAITEMENT",
  CLOTUREE = "CLOTUREE",
  ANNULEE = "ANNULEE",
}

export enum DecisionNc {
  ACCEPTATION_DEROGATION = "ACCEPTATION_DEROGATION",
  RETOUCHE = "RETOUCHE",
  REBUT = "REBUT",
  RETOUR_FOURNISSEUR = "RETOUR_FOURNISSEUR",
}

export interface NcPhoto {
  key: string;
  mimeType: string;
  originalName: string;
  uploadedAt: string;
}

export interface NcStatusHistoryEntry {
  etat: EtatNc;
  userId: string | null;
  userName: string | null;
  date: string;
  comment?: string;
}

export interface NcAdditionalLink {
  kind:
    | "lot"
    | "palette"
    | "commande_fournisseur"
    | "commande_client"
    | "chargement"
    | "tiers";
  id: string;
  label?: string;
}

export interface NonConformite extends Identifiable {
  numero?: string;
  type: TypeNc;
  gravite: GraviteNc;
  cause5M?: Cause5M;
  origine?: OrigineNc | null;
  detection?: DetectionNc;
  /** Date à laquelle le défaut s'est produit (optionnelle). */
  dateOccurrence?: Date | string | null;
  /** Date à laquelle le défaut a été constaté pour la première fois. */
  dateConstatation?: Date | string | null;
  description: string;
  dateOuverture?: Date | string;
  dateCloture?: Date | string;
  etat?: EtatNc;
  decision?: DecisionNc;
  coutEstime?: number;
  quantiteConcernee?: number | null;
  heuresRetouche?: number | null;
  /** Date d'abandon manuel de la provision avoir (Q5). Si renseignée, la
   * provision a été basculée en charge définitive et le bouton de la fiche
   * détail est masqué. */
  provisionAbandonneeDate?: Date | string | null;
  /** Liens additionnels (multiples de même type possibles, déclaratifs). */
  additionalLinks?: NcAdditionalLink[];
  photos?: NcPhoto[];
  statusHistory?: NcStatusHistoryEntry[];
  closedByUserId?: string;
  closedByUserName?: string;
  ofId?: string;
  posteId?: string;

  lotId?: string;
  lot?: Lot;
  paletteId?: string;
  palette?: Palette;
  controleId?: string;
  controle?: Controle;
  chargementId?: string;
  chargement?: Chargement;
  commandeFournisseurId?: string;
  commandeFournisseur?: Commande;
  commandeClientId?: string;
  commandeClient?: CommandeClient;
  tiersId?: string;
  tiers?: Tiers;
  articleId?: string;
  article?: Article;
  natureId?: string;
  nature?: CustomEnum;
  /** Liste documentaire (commentaires + pièces jointes) — créée à la
   *  demande au premier ajout via `vl-document-list`. `null` tant qu'aucun
   *  élément n'a été posté. */
  documentListeId?: string;
}

export interface CreateNonConformitePayload {
  type: TypeNc;
  gravite: GraviteNc;
  cause5M?: Cause5M;
  origine?: OrigineNc | null;
  detection?: DetectionNc;
  /** Date occurrence (ISO string ou Date). */
  dateOccurrence?: string | Date | null;
  /** Date constatation (ISO string ou Date). */
  dateConstatation?: string | Date | null;
  description: string;
  coutEstime?: number;
  quantiteConcernee?: number | null;
  /** Si true et lien physique présent, déclenche le mouvement vers ZNC */
  moveToZoneNc?: boolean;
  /** OF cible (déduit du contexte) — pour ZNC PDC dédiée */
  ofId?: string;
  /** Poste cible si NC sur PF d'un OF en cours */
  posteId?: string;
  /** Ligne de réception cible si NC déclarée depuis le formulaire réception. */
  receptionCommandeId?: string;
  photos?: NcPhoto[];
  /** Liens additionnels (N de même type possibles). */
  additionalLinks?: NcAdditionalLink[];
  lotId?: string;
  paletteId?: string;
  controleId?: string;
  chargementId?: string;
  commandeFournisseurId?: string;
  commandeClientId?: string;
  tiersId?: string;
  articleId?: string;
  natureId?: string;
}

export interface UpdateNonConformitePayload {
  gravite?: GraviteNc;
  cause5M?: Cause5M;
  origine?: OrigineNc | null;
  detection?: DetectionNc;
  /** Date occurrence (null = vider, undefined = ne pas toucher). */
  dateOccurrence?: string | Date | null;
  /** Date constatation (null = vider, undefined = ne pas toucher). */
  dateConstatation?: string | Date | null;
  description?: string;
  coutEstime?: number;
  quantiteConcernee?: number | null;
  photos?: NcPhoto[];
  /** Remplace l'ensemble des liens additionnels. */
  additionalLinks?: NcAdditionalLink[];
  decision?: DecisionNc;
  // Liens éditables — `null` retire le lien, `undefined` conserve.
  natureId?: string | null;
  lotId?: string | null;
  paletteId?: string | null;
  chargementId?: string | null;
  commandeFournisseurId?: string | null;
  commandeClientId?: string | null;
  tiersId?: string | null;
  articleId?: string | null;
}

export interface CloseNonConformitePayload {
  decision: DecisionNc;
  comment?: string;
  coutEstime?: number;
  /** Heures de retouche déclarées (si decision=RETOUCHE) */
  heuresRetouche?: number;
}

export interface NcStats {
  ouvertes: number;
  critiquesOuvertes: number;
  coutMois: number;
  totalMois: number;
  /** Indicateur ISO 9001 : % NC détectées en externe sur la période. null si vide. */
  tauxEchappees: number | null;
  /** Comptes par origine pour le donut de répartition. */
  origineRepartition: {
    interne: number;
    externe: number;
    mixte: number;
    nonRenseigne: number;
  };
}

export interface NcWarehouseBucket {
  adresseId: string;
  adresseDenomination: string;
  ouvertes: number;
  critiques: number;
  coutTotal: number;
  emplacements: number;
}

export interface NcAxisBucket {
  key: string;
  label: string;
  count: number;
  cout: number;
}

export interface NcAnalytics {
  totalCount: number;
  ouvertes: number;
  critiquesOuvertes: number;
  coutTotal: number;
  byType: NcAxisBucket[];
  byGravite: NcAxisBucket[];
  byEtat: NcAxisBucket[];
  byNature: NcAxisBucket[];
  byMonth: { month: string; count: number; cout: number }[];
  topTiers: NcAxisBucket[];
  topArticles: NcAxisBucket[];
  criticalOpen: {
    id: string;
    numero: string;
    type: string;
    description: string;
    dateOuverture: string;
    coutEstime: number | null;
    natureLabel: string | null;
    tiersLabel: string | null;
  }[];
  /** Délai moyen détection (constatation - occurrence) en heures. null si pas de samples. */
  delaiDetectionMoyenHeures: number | null;
  delaiDetectionSamples: number;
  /** Délai moyen enregistrement (ouverture - constatation) en heures. null si pas de samples. */
  delaiEnregistrementMoyenHeures: number | null;
  delaiEnregistrementSamples: number;
}

export type QuickSearchKind =
  | "lot"
  | "palette"
  | "commande_fournisseur"
  | "commande_client"
  | "chargement"
  | "tiers";

export interface QuickSearchDerived {
  lotId?: string;
  paletteId?: string;
  commandeFournisseurId?: string;
  commandeClientId?: string;
  chargementId?: string;
  tiersId?: string;
  articleId?: string;
}

export interface QuickSearchResult {
  kind: QuickSearchKind;
  id: string;
  label: string;
  hint?: string;
  derived: QuickSearchDerived;
}

export const QUICK_SEARCH_LABELS: Record<QuickSearchKind, string> = {
  lot: "Lot",
  palette: "Palette",
  commande_fournisseur: "Commande F.",
  commande_client: "Commande C.",
  chargement: "Chargement",
  tiers: "Tiers",
};

export const QUICK_SEARCH_ICONS: Record<QuickSearchKind, string> = {
  lot: "label",
  palette: "inventory_2",
  commande_fournisseur: "shopping_cart",
  commande_client: "shopping_basket",
  chargement: "local_shipping",
  tiers: "business",
};

export interface NcContext {
  lotId?: string;
  paletteId?: string;
  controleId?: string;
  chargementId?: string;
  commandeFournisseurId?: string;
  commandeClientId?: string;
  tiersId?: string;
  articleId?: string;
  ofId?: string;
  posteId?: string;
  /** Ligne de réception concernée si la NC est déclarée depuis le formulaire
   * de réception (Q1 redirect : single conforme + bouton NC). */
  receptionCommandeId?: string;
  type?: TypeNc;
  /** Indique que la NC porte sur le PF du lot de l'OF — masque RETOUR_FOURNISSEUR
   * dans le formulaire de clôture (le PF n'est pas retournable au fournisseur). */
  isPfNc?: boolean;
}
